// planHelpers.js — pure, side-effect-free builders for the financial "plan"
// objects that the savings/cushion UI surfaces render from. Computed once here
// so the Tax stash card, Savings reserve card, Home "Heads up" card, and the
// Cash Flow month card can never contradict each other numerically.
//
// Conventions mirror utils/dateHelpers.js: named exports, no Date.now() inside
// (today is always injected), guard every bad input / division, return null /
// empty rather than NaN. Tax math is NOT computed here — dollar inputs
// (quarterlyReserve, cushionTarget) are taken as given.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Month index (0 = Jan) → short label. Returns null for non-integer / out-of-cycle input.
 * @param {number} i
 * @returns {string|null} e.g. 'Jan' … 'Dec'
 */
export function monthIndexToLabel(i) {
  if (typeof i !== 'number' || !Number.isInteger(i)) return null;
  return MONTHS[((i % 12) + 12) % 12];
}

/**
 * Convert a 12-element income-weight curve into projected dollars per month.
 *
 *   dollars[i] = round( (monthWeights[i] / sum(monthWeights)) * annualIncome )
 *
 * Rounded to whole dollars. Use this to feed slowSeasonPlan, which requires
 * dollars — the app stores the curve as relative weights, so callers must
 * convert here (a pure helper) rather than in a component.
 *
 * @param {Object}   args
 * @param {number[]} args.monthWeights  12 relative income weights, index 0 = Jan.
 * @param {number}   args.annualIncome  Total annual income in dollars.
 * @returns {number[]|null} 12 whole-dollar values, or null when a trustworthy
 *   dollar curve can't be built — callers treat null as "hide the savings
 *   surface". Returns null when:
 *     - monthWeights is not a 12-element array of finite numbers
 *     - annualIncome is null/undefined/NaN or <= 0
 *     - sum(monthWeights) is 0 (no division base)
 *     - the resulting max monthly dollar value is still <= 20 (degenerate inputs
 *       that would trip slowSeasonPlan's weights guard downstream)
 */
export function curveToMonthlyDollars({ monthWeights, annualIncome } = {}) {
  const validWeights = Array.isArray(monthWeights)
    && monthWeights.length === 12
    && monthWeights.every((w) => typeof w === 'number' && !isNaN(w));
  if (!validWeights) return null;
  if (typeof annualIncome !== 'number' || isNaN(annualIncome) || annualIncome <= 0) return null;

  const sum = monthWeights.reduce((a, b) => a + b, 0);
  if (sum === 0) return null;

  const dollars = monthWeights.map((w) => Math.round((w / sum) * annualIncome));
  if (Math.max(...dollars) <= 20) return null; // degenerate → would trip the weights guard

  return dollars;
}

// ── internal date helpers (not exported) ─────────────────────────────────────

// Coerce a 'YYYY-MM-DD' string or Date into a local-midnight Date. null on bad input.
function toLocalMidnight(value) {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === 'string') {
    // Append midnight so 'YYYY-MM-DD' parses in local time (avoids UTC drift),
    // matching dateHelpers.js. Falls back to native parse for other formats.
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value + 'T00:00:00' : value;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
}

// Absolute month number for comparisons/arithmetic across year boundaries.
function absMonth(date) {
  return date.getFullYear() * 12 + date.getMonth();
}

// First contribution month as an absolute month number, applying the shared
// current-month rule: the current month counts only if today's date <= 15,
// otherwise the first available month is next month.
function firstContributionAbs(today) {
  return absMonth(today) + (today.getDate() > 15 ? 1 : 0);
}

// Whole calendar months available from `today` to `deadline`, inclusive of the
// deadline month and of the current month when today.getDate() <= 15. Minimum 1.
function monthsRemainingUntil(today, deadline) {
  const count = absMonth(deadline) - firstContributionAbs(today) + 1;
  return Math.max(1, count);
}

// Last day of a given month as a local-midnight Date.
function lastDayOfMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0);
}

/**
 * Quarterly tax-reserve plan.
 *
 * Month-counting rule: whole calendar months from `today` to `dueDate`,
 * inclusive of the deadline month. The current month counts only if
 * today.getDate() <= 15; otherwise counting starts next month. Never below 1.
 *
 * @param {Object}        args
 * @param {number}        args.quarterlyReserve  Dollar reserve for the quarter (taken as-is).
 * @param {string|Date}   args.dueDate           IRS quarterly due date ('YYYY-MM-DD' or Date).
 * @param {Date}          [args.today=new Date()] Injected "now" (no Date.now() inside).
 * @returns {{
 *   target: number,        // = quarterlyReserve
 *   deadline: string|Date, // = dueDate, returned unchanged
 *   monthsRemaining: number,
 *   monthlyTarget: number, // ceil(target / monthsRemaining), whole dollars
 *   status: 'on_track'|'due_now'
 * }|null} null if inputs are missing/invalid.
 */
export function taxReservePlan({ quarterlyReserve, dueDate, today = new Date() } = {}) {
  if (typeof quarterlyReserve !== 'number' || isNaN(quarterlyReserve)) return null;
  const deadlineDate = toLocalMidnight(dueDate);
  const todayDate = toLocalMidnight(today);
  if (!deadlineDate || !todayDate) return null;

  const target = quarterlyReserve;
  const dueNow = absMonth(deadlineDate) <= absMonth(todayDate); // this month or past
  const monthsRemaining = monthsRemainingUntil(todayDate, deadlineDate);
  const monthlyTarget = Math.ceil(target / monthsRemaining); // monthsRemaining >= 1, safe

  return {
    target,
    deadline: dueDate,
    monthsRemaining,
    monthlyTarget,
    status: dueNow ? 'due_now' : 'on_track',
  };
}

// Dominant circular run of underwater months for the slow-season window.
// Bridges a single isolated above-water "reprieve" month (one above-water month
// sitting directly between two underwater months) so it does NOT split the
// window; a gap of two or more above-water months DOES split. Returns the span
// (every index from the first to the last underwater month, including any
// interior reprieve months) of the run that contains the annual income minimum.
// Callers handle the all-underwater / none-underwater cases before calling this.
function underwaterWindow(incomeCurve, underwater) {
  const n = 12;

  // A lone above-water month flanked by underwater months counts toward the span.
  const effective = underwater.map(
    (u, i) => u || (underwater[(i - 1 + n) % n] && underwater[(i + 1) % n])
  );

  // Annual income minimum (earliest on tie) — the dominant window includes it.
  let minIndex = 0;
  for (let i = 1; i < n; i++) {
    if (incomeCurve[i] < incomeCurve[minIndex]) minIndex = i;
  }

  // Every month effective (e.g. alternating dips, all bridged) → whole-year span.
  if (effective.every(Boolean)) return effective.map((_, i) => i);

  // Maximal circular runs of `effective`, scanned from a rising edge so a winter
  // window wrapping Dec→Jan stays a single run.
  let startEdge = 0;
  for (let i = 0; i < n; i++) {
    if (effective[i] && !effective[(i - 1 + n) % n]) { startEdge = i; break; }
  }
  const runs = [];
  let current = [];
  for (let k = 0; k < n; k++) {
    const idx = (startEdge + k) % n;
    if (effective[idx]) {
      current.push(idx);
    } else if (current.length) {
      runs.push(current);
      current = [];
    }
  }
  if (current.length) runs.push(current);

  // The run containing the annual minimum is the dominant window.
  return runs.find((run) => run.includes(minIndex)) ?? runs[0];
}

/**
 * Slow-season cushion plan, sized to the user's actual monthly shortfalls.
 *
 * Shortfall model: for each month, shortfall = max(0, monthlyExpenses - income);
 * a month is "underwater" when shortfall > 0. The cushion target is the sum of
 * those shortfalls inside the slow-season window, so the window and the dollar
 * target agree by construction (no separate cushionTarget input).
 *
 * Window: underwater months are joined on a circular year (a winter season may
 * wrap Dec→Jan). A single isolated above-water "reprieve" month between two
 * underwater months does NOT split the window (a gap of 2+ does). The window is
 * the dominant run — the one containing the annual income minimum — and
 * slowMonths is every index in its span, including any interior reprieve month
 * (reprieve months add $0 to the target since their shortfall is 0).
 *
 * Month-counting rule (monthsRemaining): same as taxReservePlan — whole calendar
 * months from `today` to `deadline`, current month counted only if
 * today.getDate() <= 15, minimum 1. `calmMonthlyRate` instead spans the runway
 * from the end of the peak month to the deadline (min 1 month). The monthly
 * contribution is capped at the calm rate; when the cap binds the finish date
 * slides past the deadline rather than the number going up.
 *
 * @param {Object}    args
 * @param {number[]}  args.incomeCurve     12 monthly projected-income DOLLARS, index 0 = Jan (not weights).
 * @param {number}    args.monthlyExpenses Total monthly expenses in dollars.
 * @param {Date}      [args.today=new Date()]
 * @returns {(
 *   { error: 'curve_looks_like_weights' } |
 *   {
 *     status: 'no_slow_season'|'always_underwater'|'on_track'|'behind',
 *     target: number,             // sum of in-window shortfalls (0 when no season)
 *     slowMonths: number[],       // window span month indices, incl. reprieve months
 *     slowSeasonStart: number|null,
 *     deadline: Date|null,        // last day of the month before slowSeasonStart
 *     peakMonth: number|null,
 *     monthsRemaining: number|null,
 *     monthlyTarget: number|null, // capped contribution; null for degenerate states
 *     calmMonthlyRate: number|null,
 *     finishMonth: number|null,   // month index the cushion is actually full
 *     isBehind: boolean,
 *     coverageGapMonths: number[] // underwater months that begin before the cushion fills
 *   }
 * )|null} null on structurally invalid input.
 *
 * Status values:
 *   - 'no_slow_season'    income covers expenses every month — card should hide.
 *   - 'always_underwater' expenses exceed income year-round (insolvency signal,
 *                         NOT a cushion plan); target is the annual shortfall,
 *                         monthlyTarget null — show an honest message, not a plan.
 *   - 'on_track'          normal plan, cushion fills before the lean months.
 *   - 'behind'            normal plan but the cushion fills on/after they begin.
 */
export function slowSeasonPlan({ incomeCurve, monthlyExpenses, today = new Date() } = {}) {
  // Structural validation — bad input returns null (matches taxReservePlan).
  const validCurve = Array.isArray(incomeCurve)
    && incomeCurve.length === 12
    && incomeCurve.every((v) => typeof v === 'number' && !isNaN(v));
  if (!validCurve) return null;
  if (typeof monthlyExpenses !== 'number' || isNaN(monthlyExpenses)) return null;
  const todayDate = toLocalMidnight(today);
  if (!todayDate) return null;

  // Guard: values this small are almost certainly income weights, not dollars.
  if (incomeCurve.every((v) => v <= 20)) return { error: 'curve_looks_like_weights' };

  // peakMonth: highest income; ties resolve to the earliest month.
  let peakMonth = 0;
  for (let i = 1; i < 12; i++) {
    if (incomeCurve[i] > incomeCurve[peakMonth]) peakMonth = i;
  }

  // Per-month shortfall and underwater flags.
  const shortfall = incomeCurve.map((v) => Math.max(0, monthlyExpenses - v));
  const underwater = shortfall.map((s) => s > 0);
  const underwaterCount = underwater.filter(Boolean).length;

  // Degenerate: income covers expenses every month — no cushion needed.
  if (underwaterCount === 0) {
    return {
      status: 'no_slow_season',
      target: 0,
      slowMonths: [],
      slowSeasonStart: null,
      deadline: null,
      peakMonth,
      monthsRemaining: null,
      monthlyTarget: null,
      calmMonthlyRate: null,
      finishMonth: null,
      isBehind: false,
      coverageGapMonths: [],
    };
  }

  // Degenerate: underwater every month — insolvency, not a cushion problem.
  if (underwaterCount === 12) {
    const annualShortfall = Math.round(shortfall.reduce((a, b) => a + b, 0));
    return {
      status: 'always_underwater',
      target: annualShortfall,
      slowMonths: incomeCurve.map((_, i) => i),
      slowSeasonStart: 0,
      deadline: null,
      peakMonth,
      monthsRemaining: null,
      monthlyTarget: null,
      calmMonthlyRate: null,
      finishMonth: null,
      isBehind: false,
      coverageGapMonths: [],
    };
  }

  // Normal case: build the dominant circular window and size the cushion to its
  // genuine shortfalls (reprieve months contribute $0).
  const slowMonths = underwaterWindow(incomeCurve, underwater);
  const slowSeasonStart = slowMonths[0];
  const target = Math.round(slowMonths.reduce((sum, i) => sum + shortfall[i], 0));

  // deadline = last day of the month immediately before the slow season begins,
  // anchored to the upcoming season (roll a year forward if already past).
  const deadlineMonthIdx = (slowSeasonStart + 11) % 12;
  let deadline = lastDayOfMonth(todayDate.getFullYear(), deadlineMonthIdx);
  if (deadline < todayDate) deadline = lastDayOfMonth(todayDate.getFullYear() + 1, deadlineMonthIdx);

  const deadlineAbs = absMonth(deadline);
  const slowSeasonStartAbs = deadlineAbs + 1;

  // calmMonthlyRate: ideal full-runway rate from end of the peak month to the
  // deadline. Anchor the peak to the most recent occurrence on/before deadline.
  let peakAbs = deadline.getFullYear() * 12 + peakMonth;
  if (peakAbs > deadlineAbs) peakAbs -= 12;
  const calmMonths = Math.max(1, deadlineAbs - peakAbs);
  const calmMonthlyRate = Math.ceil(target / calmMonths);

  const monthsRemaining = monthsRemainingUntil(todayDate, deadline);
  const monthlyTarget = Math.min(Math.ceil(target / monthsRemaining), calmMonthlyRate);

  // finishMonth: when the cushion is actually full, given the capped contribution.
  const startAbs = firstContributionAbs(todayDate);
  const monthsNeeded = monthlyTarget > 0 ? Math.ceil(target / monthlyTarget) : null;
  const finishAbs = monthsNeeded !== null ? startAbs + monthsNeeded - 1 : null;
  const finishMonth = finishAbs !== null ? ((finishAbs % 12) + 12) % 12 : null;

  // Behind if the cushion completes on or after the first lean month.
  const isBehind = finishAbs !== null && finishAbs >= slowSeasonStartAbs;

  // The k-th window month occurs at slowSeasonStartAbs + k; flag the genuinely
  // underwater ones that begin before the cushion is full (only partly covered).
  const coverageGapMonths = isBehind
    ? slowMonths.filter((idx, k) => underwater[idx] && (slowSeasonStartAbs + k) < finishAbs)
    : [];

  return {
    status: isBehind ? 'behind' : 'on_track',
    target,
    slowMonths,
    slowSeasonStart,
    deadline,
    peakMonth,
    monthsRemaining,
    monthlyTarget,
    calmMonthlyRate,
    finishMonth,
    isBehind,
    coverageGapMonths,
  };
}
